import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus, Contact as ContactIcon, Mail, Building2, ArrowLeft, Users, Search, ChevronDown, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { useContacts, useContact, useContactMutations } from '@/hooks/contacts/useContacts';
import { getApiErrorMessage } from '@/shared/api/axios';
import { ContactFormModal } from '@/components/sales/contacts/ContactFormModal';
import { ContactDrawer } from '@/components/sales/contacts/ContactDrawer';
import type { Contact, ContactsFilters, CreateContactPayload, UpdateContactPayload } from '@/types/contacts';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/shared/api/axios';
import { isSuperAdminOrOwner } from '@jira-clone/shared-types';
import { StatTile } from '@/features/hr/components/StatTile';
import { cn, cardClassName } from '@jira-clone/ui';

export function ContactsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser ? isSuperAdminOrOwner(currentUser.role) || currentUser.role === 'ADMIN' : false;

  // ─── Search (server-side, debounced) ─────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ─── User filter (admin/superadmin only) ─────────────────────────────────────
  const [userFilter, setUserFilter] = useState('');
  const [availableUsers, setAvailableUsers] = useState<Array<{ id: string; name: string; email: string; department?: string }>>([]);

  // Fetch users for the dropdown when admin
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<{ data: Array<{ id: string; name: string; email: string; role: string; department?: string }> }>('/api/users');
        if (cancelled) return;
        // Only include sales department and superadmin roles
        const filtered = data.data.filter((u) => {
          const dept = (u.department || '').toLowerCase();
          const isSales = dept === 'sales' || dept === 'Sales';
          const isSuper = isSuperAdminOrOwner(u.role);
          return isSales || isSuper;
        });
        setAvailableUsers(filtered);
      } catch {
        // silently ignore
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  const filters = useMemo(() => {
    const f: ContactsFilters = {};
    if (search) f.search = search;
    if (isAdmin && userFilter) f.createdBy = userFilter;
    return f;
  }, [search, userFilter, isAdmin]);

  const { data: contacts = [], isLoading, isError } = useContacts(filters);
  const { createContact, updateContact, deleteContact } = useContactMutations();

  // ─── Deep-link query param support ─────────────────────────────────────────
  const qId = searchParams.get('contactId') || searchParams.get('id') || null;
  const isNew = searchParams.get('new') === 'true' || searchParams.get('action') === 'new';
  const { data: deepContact } = useContact(qId);

  // ─── UI state ───────────────────────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [editContact, setEditContact] = useState<Contact | null>(null);
  const [drawerContact, setDrawerContact] = useState<Contact | null>(null);
  const [confirmContact, setConfirmContact] = useState<Contact | null>(null);

  useEffect(() => {
    if (isNew) {
      setFormOpen(true);
    }
  }, [isNew]);

  useEffect(() => {
    if (!qId) return;
    if (deepContact) {
      setDrawerContact(deepContact);
      return;
    }
    if (contacts.length > 0) {
      const found = contacts.find((c) => c.id === qId || (c as any)._id === qId);
      if (found) setDrawerContact(found);
    }
  }, [qId, deepContact, contacts]);

  // ─── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = contacts.length;
    const withEmail = contacts.filter((c) => c.email).length;
    const uniqueCompanies = new Set(contacts.map((c) => c.company).filter(Boolean)).size;
    return { total, withEmail, uniqueCompanies };
  }, [contacts]);

  const visible = useMemo(() => contacts, [contacts]);

  // ─── Prepared-by filter options ─────────────────────────────────────────────
  // Sales users plus whoever actually created a contact, so the dropdown always
  // lists the people the current data could be filtered down to.
  const userOptions = useMemo(() => {
    const byId = new Map<string, string>();
    availableUsers.forEach((u) => byId.set(u.id, `${u.name}${u.department ? ` — ${u.department}` : ''}`));
    contacts.forEach((c: any) => {
      const id = c.created_by || c.createdBy;
      if (id && !byId.has(id) && c.creator_name) byId.set(id, c.creator_name);
    });
    return [
      { value: '', label: 'All Prepared By' },
      ...Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    ];
  }, [availableUsers, contacts]);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleCreate = () => {
    setEditContact(null);
    setFormOpen(true);
  };

  const handleEdit = (contact: Contact) => {
    setDrawerContact(null);
    setEditContact(contact);
    setFormOpen(true);
  };

  const handleSubmit = (payload: CreateContactPayload | UpdateContactPayload) => {
    if (editContact) {
      updateContact.mutate(
        { id: editContact.id, payload: payload as UpdateContactPayload },
        {
          onSuccess: () => {
            toast.success('Contact updated.');
            setFormOpen(false);
            setEditContact(null);
          },
          onError: (err) => toast.error(getApiErrorMessage(err, 'update this contact')),
        },
      );
    } else {
      createContact.mutate(payload as CreateContactPayload, {
        onSuccess: () => {
          toast.success('Contact created.');
          setFormOpen(false);
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'create this contact')),
      });
    }
  };

  const handleDelete = () => {
    if (!confirmContact) return;
    deleteContact.mutate(confirmContact.id, {
      onSuccess: () => {
        toast.success('Contact deleted.');
        setConfirmContact(null);
        handleCloseDrawer();
      },
      onError: (err) => toast.error(getApiErrorMessage(err, 'delete this contact')),
    });
  };

  const handleCloseDrawer = () => {
    setDrawerContact(null);
    if (searchParams.has('contactId') || searchParams.has('id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('contactId');
      next.delete('id');
      navigate({ search: next.toString() ? `?${next.toString()}` : '' }, { replace: true });
    }
  };

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto pr-1">
      {/* Header - matching HR pattern with gradient icon circle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 hover:shadow-md transition-shadow"
            title="Back"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200">
            <ContactIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <span className="block text-[11px] font-bold tracking-wider text-[#6825F5] uppercase">
              CONTACTS
            </span>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-[#11183F]">
              Manage your customer and prospect database.
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2.5 self-start sm:self-auto">
          <Button
            onClick={handleCreate}
            className="bg-[#6825F5] hover:bg-[#581cd6] text-white font-semibold shadow-sm shadow-purple-200"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span>New Contact</span>
          </Button>
        </div>
      </div>

      {/* KPI Stats - using StatTile from HR components */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatTile
          icon={ContactIcon}
          label="Total Contacts"
          value={stats.total}
          tone="primary"
          sublabel="In your database"
          index={0}
        />
        <StatTile
          icon={Mail}
          label="With Email"
          value={stats.withEmail}
          tone="info"
          sublabel="Reachable by email"
          index={1}
        />
        <StatTile
          icon={Building2}
          label="Companies"
          value={stats.uniqueCompanies}
          tone="success"
          sublabel="Distinct organizations"
          index={2}
        />
      </div>

      {/* Filters - HR pattern */}
      <div className="rounded-2xl border border-[#E8E8F5] bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#747C9C]" />
            <input
              type="text"
              name="contacts-search"
              placeholder="Search name, company, email, phone…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] placeholder:text-[#747C9C] focus:outline-none focus:ring-2 focus:ring-[#6825F5] focus:border-transparent"
            />
          </div>

          {isAdmin && (
            <div className="relative min-w-[180px]">
              <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                aria-label="Filter by prepared by"
                className="w-full pl-3 pr-8 py-1.5 text-xs font-medium bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] focus:outline-none focus:ring-2 focus:ring-[#6825F5] focus:border-transparent cursor-pointer appearance-none shadow-xs"
              >
                {userOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-[#747C9C]">
                <ChevronDown className="h-3.5 w-3.5" />
              </div>
            </div>
          )}

          {/* Clear Filters */}
          {(search || userFilter) && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setUserFilter('');
              }}
              className="text-xs font-semibold text-[#6825F5] hover:text-[#581cd6] px-2.5 py-1.5 rounded-lg border border-dashed border-[#A5B4FC] hover:bg-[#EDE9FE] transition-colors"
              title="Reset all filters"
            >
              <X className="h-3 w-3 mr-1 inline" />
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner className="h-6 w-6 text-[#6825F5]" />
        </div>
      ) : isError ? (
        <div className="rounded-2xl border border-[#E8E8F5] bg-white p-8 shadow-sm">
          <EmptyState title="Failed to load contacts" description="Check your connection or try refreshing." />
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-[#E8E8F5] bg-white p-8 shadow-sm">
          <EmptyState
            title={search || (isAdmin && userFilter) ? 'No matching contacts' : 'No contacts yet'}
            description={
              search || (isAdmin && userFilter)
                ? 'Try adjusting your search or filter.'
                : 'Create your first contact to build the customer database.'
            }
            action={!search && !(isAdmin && userFilter) ? (
              <Button onClick={handleCreate} className="bg-[#6825F5] hover:bg-[#581cd6] text-white shadow-sm shadow-purple-200">
                <Plus className="h-4 w-4 mr-2" />
                New Contact
              </Button>
            ) : undefined}
          />
        </div>
      ) : (
        <div className="rounded-2xl border border-[#E8E8F5] bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-[#F8F9FE] text-[#747C9C] font-bold text-[11px] tracking-wider uppercase border-b border-[#F1F3F9]">
                <tr>
                  <th className="px-5 py-3.5">Contact</th>
                  <th className="px-4 py-3.5">Company</th>
                  <th className="px-4 py-3.5">Email</th>
                  <th className="px-4 py-3.5">Phone</th>
                  <th className="px-4 py-3.5">Created By</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F3F9]">
                {visible.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-[#F8F9FE]/60 transition-colors cursor-pointer"
                    onClick={() => setDrawerContact(c)}
                  >
                    {/* Contact Name with Avatar */}
                    <td className="px-5 py-3.5 font-medium">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold bg-[#EDE9FE] text-[#6825F5]">
                          <ContactIcon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <span className="block text-sm font-semibold text-[#11183F] leading-tight truncate max-w-xs">
                            {c.name}
                          </span>
                          {c.position && (
                            <span className="text-[10px] text-[#747C9C] truncate block max-w-xs">
                              {c.position}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Company */}
                    <td className="px-4 py-3.5 text-sm text-[#475569]">
                      {c.company ? (
                        <span className="font-medium text-[#11183F]">{c.company}</span>
                      ) : (
                        <span className="text-[#747C9C] italic">—</span>
                      )}
                    </td>

                    {/* Email */}
                    <td className="px-4 py-3.5 text-sm text-[#475569]">
                      {c.email ? (
                        <a
                          href={`mailto:${c.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[#6825F5] hover:underline font-mono"
                        >
                          {c.email}
                        </a>
                      ) : (
                        <span className="text-[#747C9C] italic">—</span>
                      )}
                    </td>

                    {/* Phone */}
                    <td className="px-4 py-3.5 text-sm text-[#475569]">
                      {c.phone ? (
                        <a
                          href={`tel:${c.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-mono tabular-nums text-[#11183F] hover:text-[#6825F5]"
                        >
                          {c.phone}
                        </a>
                      ) : (
                        <span className="text-[#747C9C] italic">—</span>
                      )}
                    </td>

                    {/* Created By */}
                    <td className="px-4 py-3.5 text-sm text-[#475569]">
                      {c.creator_name || '—'}
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(c);
                          }}
                          className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-[#EDE9FE] px-3 py-1 text-xs font-semibold text-[#6825F5] hover:bg-[#DDD6FE] transition-colors cursor-pointer"
                        >
                          <ContactIcon className="h-3 w-3" />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmContact(c);
                          }}
                          className="p-1.5 text-[#747C9C] hover:text-[#DC2626] hover:bg-[#FEE2E2] rounded-lg transition-colors cursor-pointer"
                          title="Delete"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit */}
      <ContactFormModal
        open={formOpen}
        contact={editContact}
        loading={createContact.isPending || updateContact.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditContact(null);
        }}
        onSubmit={handleSubmit}
      />

      {/* Detail */}
      <ContactDrawer
        contact={drawerContact}
        onClose={handleCloseDrawer}
        onEdit={handleEdit}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!confirmContact}
        title="Delete contact"
        message={`Delete "${confirmContact?.name ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteContact.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmContact(null)}
      />
    </div>
  );
}